import { useState } from 'react';
import { useRecoilValue, useSetRecoilState } from 'recoil';
import { useMutation } from '@apollo/client';
import { OverlayTrigger, Tooltip } from 'react-bootstrap';

import * as K from 'common/constants';
import ELEMENT, { createTestId } from 'common/element';
import { MenuItemConfig } from 'common/types';
import { AccountData, Permission } from 'common/graphqlTypes';
import { useScalyrLogger } from 'common/ScalyrLogger';
import { useLogout } from 'hooks/useLogout';

import {
  accountDataState,
  activeTeamState,
  hasPermissionState,
  isManageGroupsEnabledState,
  isTeamDisplayedState,
  recentNonactiveTeamsState,
  userMenuDisplayNameState,
} from 'components/account/account.state';
import Dropdown from 'components/common/Dropdown/Dropdown';
import Link from 'components/common/Link/Link';
import { SET_LINK_MUTATION } from 'components/linkAccount/queries';
import { useTeamSwitchReload } from 'components/account/TeamSwitchDialog/TeamSwitchDialog';
import { quickSwitchLinkAccountPageUrlState } from 'components/common/Managers/RouteStateManager.state';
import UpdateTeamNameDialog, {
  updateTeamNameDialogLocalState,
} from 'components/teamAdministration/UpdateTeamNameDialog';

import UserPreferencesDialog from './UserPreferencesDialog';

import './NavBarUserMenu.scss';

/**
 * The user menu dropdown for the navbar
 *
 * @returns {Fragment}          The User Menu dropdown component
 */
const NavBarUserMenu = (): JSX.Element => {
  const [showUserPreferencesDialog, setShowUserPreferencesDialog] = useState(false);
  const { sendLogsAndStopLogging } = useScalyrLogger();
  const teamSwitchReload = useTeamSwitchReload();
  const [switchTeams] = useMutation(SET_LINK_MUTATION, {
    onCompleted: () => {
      teamSwitchReload();
    },
  });
  const logout = useLogout();

  const account = useRecoilValue(accountDataState) || ({} as AccountData);
  const activeTeam = useRecoilValue(activeTeamState);
  const recentlyUsedTeams = useRecoilValue(recentNonactiveTeamsState);
  const isTeamDisplayed = useRecoilValue(isTeamDisplayedState);
  const { email, isSingularityXDRUser } = account;
  const userMenuDisplayName = useRecoilValue(userMenuDisplayNameState);
  const isManageGroupsEnabled = useRecoilValue(isManageGroupsEnabledState);
  const quickSwitchLinkAccountPageUrl = useRecoilValue(quickSwitchLinkAccountPageUrlState);
  const setUpdateTeamNameDialogLocalState = useSetRecoilState(updateTeamNameDialogLocalState);
  const openUpdateTeamNameDialog = () => {
    setUpdateTeamNameDialogLocalState({
      showEditTeamNameDialog: true,
      teamNameDraft: activeTeam.displayName,
    });
  };

  const hasPermission: Record<Permission, boolean> = {} as Record<Permission, boolean>;
  Object.values(Permission).forEach((permission) => {
    hasPermission[permission as Permission] = false;
  });
  hasPermission[Permission.limited] = useRecoilValue(hasPermissionState(Permission.limited));
  hasPermission[Permission.logRead] = useRecoilValue(hasPermissionState(Permission.logRead));
  hasPermission[Permission.userConfigWrite] = useRecoilValue(hasPermissionState(Permission.userConfigWrite));
  hasPermission[Permission.fullAccess] = useRecoilValue(hasPermissionState(Permission.fullAccess));

  const hasXdrUserPermission = useRecoilValue(hasPermissionState(Permission.xdrUser));

  let quickSwitchTeams = [] as Array<MenuItemConfig>; // The list of teams the user can switch to
  const teamSwitchHeader = [] as Array<MenuItemConfig>; // The header for the switch menu as well as the activeTeam selection (if any)

  if (recentlyUsedTeams.length > 0) {
    teamSwitchHeader.push({
      text: 'Team:',
      as: 'div',
      className: 'dropdown-header',
      testId: ELEMENT.navUserTeam,
    });
    if (activeTeam) {
      if (activeTeam.displayName.toLowerCase() === activeTeam.emailAddress.toLowerCase()) {
        teamSwitchHeader.push({ text: activeTeam.displayName, active: true, testId: ELEMENT.navUserActiveTeam });
      } else {
        // If the active team name does not match the email address, show the email address in a tooltip
        teamSwitchHeader.push({
          children: (
            <OverlayTrigger
              placement="left"
              overlay={<Tooltip id={`NavBarUserMenu__Team: ${activeTeam.id}`}>{activeTeam.emailAddress}</Tooltip>}
            >
              <span>{activeTeam.displayName}</span>
            </OverlayTrigger>
          ),
          text: activeTeam.displayName,
          active: true,
          testId: ELEMENT.navUserActiveTeam,
        });
      }
    }
    // recentlyUsedTeams doesn't include the currently active team. We show the activeTeam selected at the top of this
    // section, so we don't need to include it in the list of teams available to switch to.
    quickSwitchTeams = recentlyUsedTeams.map((team, index) => {
      const className = 'NavBarUserMenu__Team--Inactive';
      const testId = createTestId(ELEMENT.navUserSwitchTeam, index);
      const clickHandler = () => {
        sendLogsAndStopLogging();
        switchTeams({ variables: { targetAccountEmail: team.emailAddress } });
      };
      if (team.displayName.toLowerCase() === team.emailAddress.toLowerCase()) {
        return {
          text: team.displayName,
          as: 'button',
          className,
          testId,
          clickHandler,
        };
      }
      // If the team name does not match the email address, show the email address in a tooltip
      return {
        children: (
          <OverlayTrigger
            key={team.id}
            placement="left"
            overlay={<Tooltip id={`NavBarUserMenu__Team: ${team.id}-${index}`}>{team.emailAddress}</Tooltip>}
          >
            <div>{team.displayName}</div>
          </OverlayTrigger>
        ),
        text: team.displayName,
        as: 'button',
        className,
        testId,
        clickHandler,
      };
    });
  }

  const moreButton: MenuItemConfig = {
    text: '...more',
    className: 'NavBarUserMenu__MoreButton',
    href: quickSwitchLinkAccountPageUrl,
    testId: ELEMENT.navUserMoreTeams,
  };

  // Build the team quick switch section of the user Menu.
  const manageTeamsItem = {
    text: 'Manage Teams',
    href: quickSwitchLinkAccountPageUrl,
    testId: ELEMENT.navUserManageTeams,
    disabledForSingularityXDR: true,
  };
  const manageGroupsItem = {
    text: 'Manage Groups',
    href: K.ROUTE.MANAGE_GROUPS,
    testId: ELEMENT.navUserManageGroups,
    disabledForSingularityXDR: true,
  };
  const manageUsersItem = {
    text: 'Manage Users',
    href: K.ROUTE.MANAGE_USERS,
    testId: ELEMENT.navUserManageUsers,
    disabledForSingularityXDR: true,
  };
  const editTeamNameItem = {
    text: 'Edit Team Name',
    as: 'button',
    testId: ELEMENT.navUserUpdateTeamName,
    clickHandler: openUpdateTeamNameDialog,
    disabledForSingularityXDR: true,
  };
  const switchTeamsSection = teamSwitchHeader.concat(
    quickSwitchTeams.length > 3 ? [...quickSwitchTeams.slice(0, 3), moreButton] : quickSwitchTeams,
  );

  if (isManageGroupsEnabled) {
    switchTeamsSection.push(manageGroupsItem);
  }

  if (hasPermission[Permission.userConfigWrite]) {
    switchTeamsSection.push(manageUsersItem);
  }

  switchTeamsSection.push(manageTeamsItem);

  if (hasPermission[Permission.fullAccess]) {
    switchTeamsSection.push(editTeamNameItem);
  }

  const onLogout = () => {
    sendLogsAndStopLogging();
    logout();
  };

  const switchSection: MenuItemConfig[] = [
    {
      text: 'Preferences',
      as: 'button',
      testId: ELEMENT.navUserPreferences,
      clickHandler: () => {
        setShowUserPreferencesDialog(true);
      },
    },
  ];

  const buildInfo = (
    <div className="NavBarUserMenu__BuildInfo" data-test-id={ELEMENT.navUserBuildInfo}>
      <div>{`Build Hash: ${window.BUILD_INFO.buildHash}`}</div>
      <div>{`Build Date: ${window.BUILD_INFO.buildDate}`}</div>
    </div>
  );

  const userMenuSections = [
    [
      {
        config: {
          text: K.PRODUCT_NAME + ' Labs',
          href: K.ROUTE.LABS,
          testId: ELEMENT.navUserLabs,
          disabledForSingularityXDR: true,
        },
        permission: Permission.limited,
      },
      {
        config: {
          text: 'Config Files',
          href: K.ROUTE.CONFIG,
          testId: ELEMENT.navUserConfigFiles,
          disabledForSingularityXDR: !hasXdrUserPermission || false,
        },
        permission: Permission.logRead,
      },
      {
        config: {
          text: 'Secrets',
          href: K.ROUTE.SECRETS,
          testId: ELEMENT.navUserSecrets,
        },
        permission: Permission.logRead,
      },
    ],
    [
      {
        config: {
          text: 'Log Processing',
          href: K.ROUTE.LOG_PROCESSING,
          testId: ELEMENT.navUserScrubbing,
          disabledForSingularityXDR: !hasXdrUserPermission || false,
        },
        permission: Permission.userConfigWrite,
      },
      {
        config: {
          text: 'Parsers',
          href: K.ROUTE.PARSERS,
          testId: ELEMENT.navUserParsers,
          disabledForSingularityXDR: !hasXdrUserPermission || false,
        },
        permission: Permission.logRead,
      },
      {
        config: {
          text: 'Monitors',
          href: K.ROUTE.MONITORS_PAGE,
          testId: ELEMENT.navUserMonitors,
          disabledForSingularityXDR: !hasXdrUserPermission || false,
        },
        permission: Permission.userConfigWrite,
      },
      {
        config: {
          text: 'API Keys',
          href: K.ROUTE.KEYS,
          testId: ELEMENT.navUserApiKeys,
          disabledForSingularityXDR: !hasXdrUserPermission || false,
        },
        permission: Permission.fullAccess,
      },
      {
        config: {
          text: 'Batch Export to S3',
          href: K.ROUTE.BULK_EXPORTS,
          testId: ELEMENT.navUserExport,
          disabledForSingularityXDR: true,
        },
        permission: Permission.userConfigWrite,
      },
    ],
    [
      {
        config: {
          text: 'Billing Plan',
          href: K.ROUTE.PLAN,
          testId: ELEMENT.navUserBilling,
          disabledForSingularityXDR: !hasXdrUserPermission || false,
        },
        permission: Permission.fullAccess,
      },
      {
        config: {
          text: 'Credit Card',
          href: K.ROUTE.UPDATE_CREDIT_CARD,
          testId: ELEMENT.navUserCreditCard,
          disabledForSingularityXDR: true,
        },
        permission: Permission.fullAccess,
      },
    ],
  ];

  // within each section, check for required permission; drop empty sections and return the configs
  const sectionsWithAccess: MenuItemConfig[][] = userMenuSections
    .map((section) =>
      section
        .filter((item) => !item.config.disabledForSingularityXDR || hasPermission[item.permission])
        .map((item) => item.config),
    )
    .filter((section) => section.length > 0);

  const sections = [
    switchSection,
    switchTeamsSection,
    ...sectionsWithAccess,
    [
      {
        text: 'System Status',
        href: 'https://status.scalyr.com/',
        openInNewTab: true,
        testId: ELEMENT.navUserSystemStatus,
        disabledForSingularityXDR: false,
      },
    ],
    [
      {
        text: 'Change Password',
        href: K.ROUTE.CHANGE_PASSWORD,
        testId: ELEMENT.navUserChangePassword,
        disabledForSingularityXDR: true,
      },
      {
        text: 'Log Out',
        as: 'button',
        testId: ELEMENT.navUserLogOut,
        disabledForSingularityXDR: false,
        clickHandler: () => {
          onLogout();
        },
      },
      { children: buildInfo, as: 'div' },
    ],
  ];

  const updatedSections = Boolean(isSingularityXDRUser)
    ? sections.map((section) => section.filter((item) => !item.disabledForSingularityXDR))
    : sections;

  const title = isTeamDisplayed ? (
    <div>
      <div>{email}</div>
      <div className="NavBarUserMenu__ActiveTeam">{userMenuDisplayName}</div>
    </div>
  ) : (
    email || ''
  );

  const updateTeamNameDialog = hasPermission[Permission.fullAccess] ? <UpdateTeamNameDialog /> : null;

  return (
    <>
      <Dropdown isNav sections={updatedSections} itemAs={Link} testId={ELEMENT.navUserButton}>
        {title}
      </Dropdown>

      {updateTeamNameDialog}

      <UserPreferencesDialog show={showUserPreferencesDialog} closeDialog={() => setShowUserPreferencesDialog(false)} />
    </>
  );
};

export default NavBarUserMenu;
